/**
 * What each role may do, transcribed from the Role columns of
 * contracts/partner-api.md. The server enforces all of it (403
 * ROLE_FORBIDDEN); this table only decides what the UI offers, so a developer
 * never sees a "Leads" link that would answer 403.
 */
import type { PartnerRole } from '../api/types';

export const CAPABILITIES = {
  /** Sites, origins, snippet, verification — the Developer's whole world. */
  sites: ['owner', 'admin', 'developer'],
  rotateSiteKey: ['owner', 'admin'],
  /** Pause / resume the widget on a key pair (`POST /sites/:key/pause|resume`). */
  pauseSite: ['owner', 'admin', 'developer'],
  /** Ask FinderlyFix to close the organisation and delete its data (`/partner/org/close-request`). */
  closeOrg: ['owner'],
  /** The top bar's "+ New site" shortcut (Sites & keys itself stays open to developers). */
  addSite: ['owner', 'admin'],
  /** Rename a site (`PATCH /sites/:key { label }`); developers keep the key's website policy only. */
  renameSite: ['owner', 'admin'],
  /** Draft/publish the widget config. Developers may only read published. */
  editConfig: ['owner', 'admin'],
  destinations: ['owner', 'admin'],
  /** The contractor roster: add, edit, disable, import. */
  roster: ['owner', 'admin'],
  /** Routing rules, accept deadlines and the cascade (`/partner/routing`). */
  routing: ['owner', 'admin'],
  leads: ['owner', 'admin', 'operator'],
  /** Undo a close (`POST /partner/leads/:id/reopen`); operators close, they do not reopen. */
  reopenLead: ['owner', 'admin'],
  funnel: ['owner', 'admin', 'operator', 'billing'],
  installHealth: ['owner', 'admin', 'developer'],
  exportCsv: ['owner', 'admin'],
  /** Read-only dashboard links (`/share/:token`): create, list, revoke. */
  shareDashboard: ['owner', 'admin'],
  /** The FinderlyFix-hosted page for partners with no website (`/p/{slug}`). */
  hostedPage: ['owner', 'admin'],
  members: ['owner', 'admin'],
  audit: ['owner', 'admin'],
  editOrg: ['owner', 'admin'],
  /** Accept the Partner Terms on the organisation's behalf (`/partner/terms/accept`). */
  acceptTerms: ['owner', 'admin'],
  readOrg: ['owner', 'admin', 'developer', 'operator', 'billing'],
  /**
   * Create and revoke AI agent tokens (migration 070). A developer may: the
   * token can never do more than its creator's role allows.
   */
  agentTokens: ['owner', 'admin', 'developer'],
  /** See the widget usage limits card (migration 089). */
  usageLimits: ['owner', 'admin', 'developer', 'operator', 'billing'],
  /** Ask FinderlyFix for higher widget usage limits. */
  requestLimits: ['owner', 'admin'],
  /** Approve what an agent prepared (a lead destination). */
  approveChanges: ['owner', 'admin'],
  /** Billing: balance, statement, invoices and the billing contact (`/partner/billing`). */
  billing: ['owner', 'admin', 'billing'],
  /** Accept the Contractor Fee Schedule on the organisation's behalf (`/partner/billing/fee-schedule/accept`). */
  acceptFeeSchedule: ['owner', 'admin'],
  /** Accept a lead in the portal, send quotes and report job completion (`/partner/leads/:id/quotes`). */
  quotes: ['owner', 'admin', 'operator'],
  /** The hosted page's full profile: contact, services, hours, photos (`/pages/:slug`). */
  hostedPageProfile: ['owner', 'admin'],
} as const satisfies Record<string, readonly PartnerRole[]>;

export type Capability = keyof typeof CAPABILITIES;

export function can(role: PartnerRole | null, capability: Capability): boolean {
  return role !== null && (CAPABILITIES[capability] as readonly PartnerRole[]).includes(role);
}

export const ROLE_LABELS: Record<PartnerRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  developer: 'Developer',
  operator: 'Operator',
  billing: 'Billing',
};

export const ROLE_DESCRIPTIONS: Record<PartnerRole, string> = {
  owner: 'Everything, including granting ownership.',
  admin: 'Everything except granting ownership.',
  developer: 'Sites, domains, install and verification. Never sees leads.',
  operator: 'The leads inbox and the dashboard funnel.',
  billing: 'Billing: statements, invoices and the billing contact.',
};
